import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useFeatures, getEnabledFeatures } from './features';
import { searchPeople } from '../utils/papiSearch';
import { getPersonFullName } from '../utils/contactUtils';

const MAX_RETRIES = 2;

/**
 * Resolves display names for a set of aliases via PAPI.
 * Returns a cache mapping alias -> full name. Aliases not yet resolved
 * show as empty string; failed lookups retry up to MAX_RETRIES times.
 */
export function useResolveAliasNames(aliases: string[]): Record<string, string> {
  const [nameCache, setNameCache] = useState<Record<string, string>>({});
  const nameCacheRef = useRef(nameCache);
  nameCacheRef.current = nameCache;
  const retryCount = useRef<Record<string, number>>({});
  const [retryTrigger, setRetryTrigger] = useState(0);

  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const features = useFeatures();

  const aliasesKey = aliases.filter(Boolean).join(',');

  useEffect(() => {
    // retryTrigger is read here to re-run this effect on transient failure retry.
    void retryTrigger;
    if (!features) return;
    let cancelled = false;

    const allAliases = aliasesKey.split(',').filter(Boolean);
    const unresolved = allAliases.filter((a) => !(a in nameCacheRef.current));
    if (unresolved.length === 0) return;

    // Mark with sentinels to prevent duplicate requests.
    setNameCache((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const a of unresolved) {
        if (!(a in next)) {
          next[a] = '';
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    Promise.allSettled(
      unresolved.map(async (alias) => {
        const people = await searchPeople(
          alias,
          queryClient,
          getEnabledFeatures(features, searchParams)
        );
        const person = people?.find((p) => p.alias === alias);
        return { alias, name: person ? getPersonFullName(person) : '' };
      })
    ).then((results) => {
      if (cancelled) return;

      const resolved: Record<string, string> = {};
      const failedAliases: string[] = [];

      for (let i = 0; i < results.length; i++) {
        const r = results[i];
        if (r.status === 'fulfilled') {
          resolved[r.value.alias] = r.value.name;
        } else {
          failedAliases.push(unresolved[i]);
        }
      }

      let needsRetry = false;
      setNameCache((prev) => {
        const next = { ...prev };
        let changed = false;
        for (const [alias, name] of Object.entries(resolved)) {
          if (next[alias] !== name) {
            next[alias] = name;
            changed = true;
          }
        }
        for (const alias of failedAliases) {
          const attempts = (retryCount.current[alias] ?? 0) + 1;
          retryCount.current[alias] = attempts;
          if (attempts < MAX_RETRIES && next[alias] === '') {
            delete next[alias];
            changed = true;
            needsRetry = true;
          }
          // At max retries, leave the empty sentinel in place to stop retrying.
        }
        return changed ? next : prev;
      });

      if (needsRetry) {
        setTimeout(() => {
          if (!cancelled) setRetryTrigger((t) => t + 1);
        }, 1000);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [aliasesKey, features, queryClient, searchParams, retryTrigger]);

  return nameCache;
}
